import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IndividualLogin, BussinessLogin } from "../../images";
import {
  readStoredClientCategory,
  writeStoredClientCategory,
} from "../../utils/clientCategoryStorage";
import { getRolePath, Toaster } from "../../utils/common";
import { CLIENT_ACTION, MASTER_ACTION } from "../../redux/action";
import {
  getClientById,
  insertUpdateClient,
} from "../../redux/action/clientAction";
import { Labelbox } from "../../components";
import PhoneInput from "../../components/PhoneInput";
import ClientServiceRequestForm from "../../components/ClientServiceRequestForm/ClientServiceRequestForm";
import { BUSINESS_TYPE_OPTIONS } from "../../constants/businessTypes";
import { SERVICE_DEFAULT_STATUS } from "../../constants/clientServiceOptions";
import {
  clientHasBusinessAccess,
  mergeBusinessesIntoClient,
  promoteClientToBusinessAccount,
} from "../../utils/clientBusinessAccess";
import {
  hasCompletedServiceOnboarding,
  markServiceOnboardingCompletedInStorage,
} from "../../utils/clientOnboarding";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import { normalizeToE164 } from "../../utils/phoneUtils";
import "./select_account.css";

const emptyBusinessForm = () => ({
  business_name: "",
  business_type: "",
  business_phone: "",
  business_address_1: "",
  business_country_id: "",
  business_state_id: "",
  business_city_id: "",
  business_zipcode: "",
});

const SelectAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { syncBusinessAccess } = useClientCategoryTab();
  const { business_country: countryOptions = [], business_state: stateOptions = [] } =
    useSelector((state) => state.master || {});

  const [userData, setUserData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    } catch {
      return {};
    }
  });

  const [addedBusinesses, setAddedBusinesses] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [selectedType, setSelectedType] = useState(() =>
    readStoredClientCategory(),
  );
  const [screen, setScreen] = useState("choose");
  const [serviceCategory, setServiceCategory] = useState("individual");
  const [submitting, setSubmitting] = useState(false);
  const [submittingService, setSubmittingService] = useState(false);
  const [formData, setFormData] = useState(emptyBusinessForm);
  const [awaitingBusinessServiceForm, setAwaitingBusinessServiceForm] =
    useState(false);

  const hasBusinessAccess =
    clientHasBusinessAccess(userData) || addedBusinesses.length > 0;
  // Only real, fetched business profiles count. Legacy flat business_name / a
  // "Business" client_type must NOT auto-satisfy this, otherwise selecting the
  // Business login type would skip the "add your first business" prompt.
  const hasRegisteredBusinesses =
    addedBusinesses.length > 0 ||
    (Array.isArray(userData?.businesses) && userData.businesses.length > 0);

  const isBusinessRegisterScreen = screen === "business_register";

  const persistClientAsBusinessAccount = async (clientId, mergedClient) => {
    const promoted = promoteClientToBusinessAccount(mergedClient, mergedClient.businesses);
    localStorage.setItem("USER_DATA", JSON.stringify(promoted));
    setUserData(promoted);
    syncBusinessAccess(promoted);

    await insertUpdateClient({
      params: {
        client_type: "Business",
        business_registered: true,
      },
      insertUpdateFlag: 1,
      client_id: clientId,
    });
  };

  const refreshClientBusinesses = async (clientId) => {
    const businesses = await CLIENT_ACTION.getClientBusinesses(clientId);
    const detail = await dispatch(getClientById(clientId)).unwrap();
    const merged = promoteClientToBusinessAccount(
      mergeBusinessesIntoClient(detail || userData, businesses),
      businesses,
    );
    setAddedBusinesses(businesses);
    await persistClientAsBusinessAccount(clientId, merged);
    return { businesses, merged };
  };

  useEffect(() => {
    const clientId = localStorage.getItem("user_id");
    if (!clientId) {
      setLoadingProfile(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const businesses = await CLIENT_ACTION.getClientBusinesses(clientId);
        if (cancelled) return;
        const detail = await dispatch(getClientById(clientId)).unwrap();
        if (cancelled) return;
        const merged = promoteClientToBusinessAccount(
          mergeBusinessesIntoClient(detail || userData, businesses),
          businesses,
        );
        setAddedBusinesses(businesses);
        setUserData(merged);
        if (businesses.length > 0) {
          localStorage.setItem("USER_DATA", JSON.stringify(merged));
          syncBusinessAccess(merged);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    if (loadingProfile) return;
    if (location.state?.intent !== "business") return;
    if (hasRegisteredBusinesses) return;
    setSelectedType("business");
    setAwaitingBusinessServiceForm(true);
    setScreen("business_warning");
    setFormData(emptyBusinessForm());
  }, [loadingProfile, location.state, hasRegisteredBusinesses]);

  useEffect(() => {
    if (!isBusinessRegisterScreen) return;
    dispatch(MASTER_ACTION.getMasterData({ name: "country" }));
  }, [dispatch, isBusinessRegisterScreen]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessCountryChange = (value) => {
    updateField("business_country_id", value);
    updateField("business_state_id", "");
    if (value) {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          name_type: "business_state",
          id: value,
        }),
      );
    }
  };

  const goToServiceForm = (category) => {
    const nextCategory = category === "business" ? "business" : "individual";
    setServiceCategory(nextCategory);
    writeStoredClientCategory(nextCategory);
    setScreen("service_form");
  };

  const handleSelect = (type) => {
    const next = type === "business" ? "business" : "individual";
    setSelectedType(next);

    if (next === "individual") {
      writeStoredClientCategory("individual");
      setAwaitingBusinessServiceForm(false);
      if (hasCompletedServiceOnboarding(userData)) {
        navigate(getRolePath("/dashboard"));
      } else {
        goToServiceForm("individual");
      }
      return;
    }

    if (!hasRegisteredBusinesses) {
      setAwaitingBusinessServiceForm(true);
      setScreen("business_warning");
      setFormData(emptyBusinessForm());
      return;
    }

    writeStoredClientCategory("business");
    if (hasCompletedServiceOnboarding(userData)) {
      navigate(getRolePath("/dashboard"));
    } else {
      goToServiceForm("business");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const {
      business_name,
      business_type,
      business_phone,
      business_address_1,
      business_country_id,
      business_state_id,
      business_city_id,
      business_zipcode,
    } = formData;

    if (!business_name.trim() || !business_type) {
      Toaster.warning("Please enter business name and type.");
      return;
    }

    const clientId = localStorage.getItem("user_id");
    if (!clientId) {
      Toaster.error("Unable to identify your account. Please sign in again.");
      return;
    }

    setSubmitting(true);
    try {
      const normalizeNullableInt = (val) => {
        if (val === "" || val === undefined || val === null) return null;
        return val;
      };

      const normalizedBusinessPhone = business_phone
        ? normalizeToE164(business_phone) || business_phone.trim()
        : "";

      const payload = {
        client_id: clientId,
        business_name: business_name.trim(),
        business_type,
        business_phone: normalizedBusinessPhone,
        business_address_1: business_address_1.trim(),
        business_country_id: normalizeNullableInt(business_country_id),
        business_state_id: normalizeNullableInt(business_state_id),
        business_city_id: business_city_id.trim(),
        business_zipcode: business_zipcode.trim(),
        is_primary: addedBusinesses.length === 0,
      };

      const result = await CLIENT_ACTION.createClientBusiness(payload);

      if (result?.status === 1) {
        const { businesses, merged } = await refreshClientBusinesses(clientId);
        writeStoredClientCategory("business");
        setFormData(emptyBusinessForm());
        Toaster.success(
          businesses.length > 1
            ? "Business added. You can add another or continue."
            : "Business account registered.",
        );
        if (awaitingBusinessServiceForm) {
          setAwaitingBusinessServiceForm(false);
          goToServiceForm("business");
        } else if (hasCompletedServiceOnboarding(merged)) {
          setScreen("business_done");
        } else {
          goToServiceForm("business");
        }
      } else {
        Toaster.error(result?.message || "Failed to add business.");
      }
    } catch (err) {
      console.error(err);
      Toaster.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceSubmit = async (form) => {
    if (!form?.service_name || !form?.tax_year || !form?.service_frequency) {
      Toaster.warning("Please complete all service fields.");
      return;
    }

    const clientId = localStorage.getItem("user_id");
    if (!clientId) {
      Toaster.error("Unable to identify your account. Please sign in again.");
      return;
    }

    setSubmittingService(true);
    try {
      const res = await CLIENT_ACTION.requestClientService({
        client_id: clientId,
        start_date: form.start_date,
        service_category: form.service_category,
        service_name: form.service_name,
        tax_year: form.tax_year,
        service_frequency: form.service_frequency,
        status: SERVICE_DEFAULT_STATUS,
        is_onboarding: false,
        ...(String(form.service_category).toLowerCase() === "business"
          ? { client_business_id: form.client_business_id }
          : {}),
      });

      if (res?.status === 1) {
        writeStoredClientCategory(form.service_category);
        Toaster.success(res.message || "Service request sent successfully");
        navigate(getRolePath("/my_services"));
      } else {
        Toaster.error(res?.message || "Could not send service request");
      }
    } catch (err) {
      console.error(err);
      Toaster.error("An error occurred. Please try again.");
    } finally {
      setSubmittingService(false);
    }
  };

  const firstNameGreet = localStorage.getItem("first_name") || "";

  if (loadingProfile) {
    return (
      <div className="select-account-page centered-screen">
        <div className="select-account-container">
          <p className="select-subheading">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`select-account-page ${
        isBusinessRegisterScreen ? "business-register-screen" : "centered-screen"
      }`}
    >
      <div className="select-account-container">
        <div className="select-account-inner">
          <div className="logo-wrapper">
            <img
              src="/resources/images/Finlotax.png"
              alt="Finlotax Logo"
              className="logo"
            />
          </div>

          {screen === "choose" ? (
            <>
              {firstNameGreet && (
                <h1 className="select-greeting">Hi {firstNameGreet}!</h1>
              )}
              <h2 className="select-heading">Choose how you&apos;d like to log in</h2>
              <p className="select-subheading">
                Your selection applies across Documents, Messages, Payments,
                Services and more. Register one or more businesses to use a
                business account.
              </p>

              <div className="cards-wrapper">
                <button
                  type="button"
                  className={`account-card ${selectedType === "individual" ? "selected" : ""}`}
                  onClick={() => handleSelect("individual")}
                >
                  <h3 className="card-title">Individual & Self-Employed</h3>
                  <p className="card-subtitle">
                    Form 1040, Sole Proprietors & Single-Member LLCs
                  </p>
                  <div className="card-icon-container">
                    <img
                      src={IndividualLogin}
                      alt="Individual Login"
                      className="card-icon"
                    />
                  </div>
                </button>

                <button
                  type="button"
                  className={`account-card ${selectedType === "business" ? "selected" : ""}`}
                  onClick={() => handleSelect("business")}
                >
                  <h3 className="card-title">Business & Organizations</h3>
                  <p className="card-subtitle">
                    Multi-Member LLCs, S Corp, C Corp & Nonprofits
                  </p>
                  <div className="card-icon-container">
                    <img
                      src={BussinessLogin}
                      alt="Business Login"
                      className="card-icon"
                    />
                  </div>
                </button>
              </div>
            </>
          ) : screen === "service_form" ? (
            <div className="registration-form-card business-registration-form">
              <h2 className="form-title">Request your service</h2>
              <ClientServiceRequestForm
                defaultCategory={serviceCategory}
                lockCategory
                clientId={localStorage.getItem("user_id")}
                businesses={addedBusinesses}
                submitting={submittingService}
                onSubmit={handleServiceSubmit}
                onCancel={() => setScreen("choose")}
              />
            </div>
          ) : screen === "business_done" ? (
            <div className="registration-success-card">
              <div className="success-icon-wrapper">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#28a745"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h3 className="success-title">Business Account Ready</h3>
              <p className="success-text">
                Your business account is set up. You can manage businesses anytime
                from your profile.
              </p>
              <button
                type="button"
                className="success-back-btn"
                onClick={() => navigate(getRolePath("/dashboard"))}
              >
                Go to Dashboard
              </button>
            </div>
          ) : screen === "business_register" ? (
            <div className="registration-form-card business-registration-form">
              <h2 className="form-title">Register your business account</h2>
              <p className="form-subtitle">
                Add your business details before selecting your service.
              </p>

              {addedBusinesses.length > 0 && (
                <div className="select-account-business-list">
                  <h3 className="form-section-title">Your businesses</h3>
                  <ul>
                    {addedBusinesses.map((business) => (
                      <li key={business.client_business_id || business.business_name}>
                        <span className="select-account-business-name">
                          {business.business_name}
                        </span>
                        <span className="select-account-business-type">
                          {business.business_type}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="inline-reg-form">
                <h3 className="form-section-title">Business details</h3>

                <div className="form-row-double">
                  <div className="form-field">
                    <Labelbox
                      type="text"
                      labelname="Business Name"
                      value={formData.business_name}
                      changeData={(val) => updateField("business_name", val)}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Labelbox
                      type="select"
                      labelname="Type"
                      value={formData.business_type}
                      changeData={(val) => updateField("business_type", val)}
                      dropdown={BUSINESS_TYPE_OPTIONS}
                      required
                    />
                  </div>
                </div>

                <div className="form-row-single">
                  <PhoneInput
                    labelname="Business Phone No."
                    value={formData.business_phone || ""}
                    onChange={(val) => updateField("business_phone", val)}
                  />
                </div>

                <div className="form-row-single">
                  <Labelbox
                    type="text"
                    labelname="Address 1"
                    value={formData.business_address_1}
                    changeData={(val) => updateField("business_address_1", val)}
                  />
                </div>

                <div className="form-row-double">
                  <div className="form-field">
                    <Labelbox
                      type="select"
                      labelname="Country"
                      value={formData.business_country_id}
                      changeData={handleBusinessCountryChange}
                      dropdown={countryOptions}
                    />
                  </div>
                  <div className="form-field">
                    <Labelbox
                      type="select"
                      labelname="State Incorporated"
                      value={formData.business_state_id}
                      changeData={(val) => updateField("business_state_id", val)}
                      dropdown={stateOptions}
                      disabled={!formData.business_country_id}
                    />
                  </div>
                </div>

                <div className="form-row-double">
                  <div className="form-field">
                    <Labelbox
                      type="text"
                      labelname="City"
                      value={formData.business_city_id}
                      changeData={(val) => updateField("business_city_id", val)}
                    />
                  </div>
                  <div className="form-field">
                    <Labelbox
                      type="text"
                      labelname="Zip Code"
                      value={formData.business_zipcode}
                      changeData={(val) => updateField("business_zipcode", val)}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="form-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : "Save & continue"}
                  </button>
                  <button
                    type="button"
                    className="form-cancel-btn"
                    onClick={() => setScreen("business_warning")}
                  >
                    Back
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="warning-box-card">
              <h3 className="warning-box-title">Set up your business account</h3>
              <div className="warning-box-icon-container">
                <img
                  src={BussinessLogin}
                  alt="Business"
                  className="warning-box-icon"
                />
              </div>
              <p className="warning-box-text">
                You selected Business view, but no business is registered yet.
                Add your first business, then choose your service type.
              </p>
              <div className="warning-box-actions">
                <button
                  type="button"
                  className="warning-register-btn"
                  onClick={() => setScreen("business_register")}
                >
                  Add business
                </button>
                <button
                  type="button"
                  className="warning-back-btn"
                  onClick={() => setScreen("choose")}
                >
                  Back to selection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectAccount;
